import React from "react";
import {
  Box,
  Container,
  Grid2,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  Rule,
  Wifi,
  LocalParking,
  FitnessCenter,
  Pool,
  Kitchen,
  AcUnit,
  Security,
  CleaningServices,
  Block,
  BatteryChargingFull,
  Opacity,
  LocalLaundryService,
  Videocam,
  TwoWheeler,
  DirectionsCar,
  Elevator,
  Tv,
  LocalOffer,
} from "@mui/icons-material";

// Helper to map amenity strings to Icons
// In a real app, store the icon key in your DB or use a robust switch statement
const getAmenityIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes("wifi") || n.includes("internet")) return <Wifi />;
  //   if (n.includes("parking")) return <LocalParking />;
  // Gym / fitness
  if (n.includes("gym") || n.includes("fitness")) return <FitnessCenter />;

  // Kitchen / fridge / refrigerator
  if (
    n.includes("kitchen") ||
    n.includes("fridge") ||
    n.includes("refrigerator")
  )
    return <Kitchen />;
  // Power / backup / inverter
  if (
    n.includes("power") ||
    n.includes("backup") ||
    n.includes("inverter") ||
    n.includes("battery")
  )
    return <BatteryChargingFull />;

  // RO / purified water / drinking water
  if (
    n.includes("ro") ||
    n.includes("water") ||
    n.includes("purifier") ||
    n.includes("drinking")
  )
    return <Opacity />;

  // Laundry / washing
  if (n.includes("laundry") || n.includes("washing") || n.includes("washer"))
    return <LocalLaundryService />;

  // CCTV / camera / surveillance / security cameras
  if (n.includes("cctv") || n.includes("camera") || n.includes("surveillance"))
    return <Videocam />; // or <SecurityIcon />

  // Parking - bike or car
  if (
    n.includes("bike") ||
    n.includes("two") ||
    n.includes("two-wheeler") ||
    n.includes("two wheeler")
  )
    return <TwoWheeler />;

  if (n.includes("car") || n.includes("parking") || n.includes("car park"))
    return <DirectionsCar />;

  // Lift / elevator / stairlift
  if (n.includes("lift") || n.includes("elevator")) return <Elevator />;

  // Pool / swimming
  if (n.includes("pool") || n.includes("swimming")) return <Pool />;

  // AC / a/c / air conditioning
  if (n.includes("ac") || n.includes("air") || n.includes("aircon"))
    return <AcUnit />;

  // TV / common tv / cable
  if (
    n.includes("tv") ||
    n.includes("television") ||
    n.includes("common tv") ||
    n.includes("cable")
  )
    return <Tv />;

  // Security
  if (n.includes("security")) return <Security />;

  // Cleaning / housekeeping
  if (n.includes("clean") || n.includes("housekeeping") || n.includes("maid"))
    return <CleaningServices />;

  return <CheckCircleOutline />; // Default
};

const PropertyLifestyle = ({ amenities = [], rules = [] }) => {
  //   console.log(333, amenities);
  const theme = useTheme();

  return (
    <Container sx={{ mb: 5 }}>
      <Grid2 container spacing={4}>
        {/* --- LEFT COLUMN: COMMON AMENITIES --- */}
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Box mb={2}>
            {/* <Typography variant="h5" fontWeight="bold" gutterBottom>
              What this place offers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Common amenities accessible to all residents
            </Typography> */}
            <Typography
              variant="h5"
              component="h2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <LocalOffer color="action" fontSize="small" aria-hidden="true" />
              {/* What this place offers */}
              Features & Services
            </Typography>
            <Typography variant="h6" component="p" color="text.secondary">
              Common Amenities
            </Typography>
          </Box>

          {amenities.length > 0 ? (
            <Grid2 container spacing={2}>
              {amenities.map((amenity, index) => (
                <Grid2 size={{ xs: 6, sm: 4 }} key={index}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    p={1.5}
                    borderRadius={2}
                    sx={{
                      bgcolor: "background.paper",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {/* <Box color="primary.main" display="flex"> */}
                    <Box display="flex">
                      {getAmenityIcon(amenity.name || amenity)}
                    </Box>
                    <Typography variant="body2" fontWeight="500">
                      {amenity.name || amenity}
                    </Typography>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          ) : (
            <Typography color="text.secondary">No amenities listed.</Typography>
          )}
        </Grid2>

        {/* --- RIGHT COLUMN: HOUSE RULES --- */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: "action.hover", // Subtle background to distinguish it
              borderRadius: 3,
              height: "100%",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Rule color="error" />
              {/* <Typography variant="h6" fontWeight="bold">
                House Rules
              </Typography> */}
              <Typography
                variant="h5"
                component="h2"
                // sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                House Rules
              </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {rules.length > 0 ? (
              <>
                {/* <List disablePadding>
                  {rules.map((rule) => (
                    <ListItem key={rule._id} disableGutters sx={{ py: 1 }}>
                      <ListItemIcon
                        sx={{ minWidth: 32, mt: 0.5, alignSelf: "flex-start" }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            bgcolor: "text.secondary",
                            borderRadius: "50%",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={rule.text || rule}
                        primaryTypographyProps={{
                          variant: "body2",
                          lineHeight: 1.6,
                        }}
                      />
                    </ListItem>
                  ))}
                </List> */}

                <List dense>
                  {rules.map((rule, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Security fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={rule.ruleText} />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No specific rules listed.
              </Typography>
            )}

            <Box mt={3} p={2} bgcolor="background.paper" borderRadius={1}>
              <Typography variant="caption" color="text.secondary">
                * By booking this property, you agree to abide by these rules.
              </Typography>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Container>
  );
};

export default PropertyLifestyle;
